import { useState } from 'react'
import { ResponsiveSunburst } from '@nivo/sunburst'
import type { Impact } from '@/api/types'
import { Spinner, ErrorState, EmptyState } from '@/components/common/State'
import { useImpactTree, type RateView } from './useImpactTree'
import { RateToggle } from './RateToggle'

interface SunNode {
  name: string
  loc?: number
  children?: SunNode[]
}

function toNivo(impact: Impact): SunNode {
  if (impact.children && impact.children.length > 0) {
    return { name: impact.name || impact.id, children: impact.children.map(toNivo) }
  }
  return { name: impact.name || impact.id, loc: impact.total_at_risk }
}

// Conservation drill-down org → inv → source → asset (design §5.8, Inventory Owner lens).
export function SunburstLens({ orgId }: { orgId: string }) {
  const [rate, setRate] = useState<RateView>('total')
  const { root, isLoading, error } = useImpactTree(orgId, rate)

  if (isLoading) return <Spinner />
  if (error) return <ErrorState error={error} />
  if (!root || (root.children?.length ?? 0) === 0)
    return <EmptyState icon="donut_large" title="No $-at-risk hierarchy" hint="Author inventory economics to drill down." />

  return (
    <div>
      <RateToggle rate={rate} onChange={setRate} />
      <div className="h-[520px]">
        <ResponsiveSunburst
          data={toNivo(root)}
          id="name"
          value="loc"
          valueFormat=".02s"
          cornerRadius={3}
          borderWidth={1}
          colors={{ scheme: 'blues' }}
          childColor={{ from: 'color', modifiers: [['brighter', 0.3]] }}
          enableArcLabels
          arcLabelsSkipAngle={12}
        />
      </div>
    </div>
  )
}
